"use client"

import { ArrowDown, ArrowUp, Minus } from "lucide-react"

import { GlassCard } from "@shared/ui/landing/glass-card"
import { SectionContainer } from "@shared/ui/landing/section-container"
import { StatCounter } from "@shared/ui/landing/stat-counter"
import { StatusTag } from "@shared/ui/landing/status-tag"

import { reportFindings, reportMetrics } from "@/lib/live-data"
import { useTranslations } from "@/lib/i18n/client"

function TrendIcon({ trend }: { trend: "up" | "down" | "stable" }) {
  if (trend === "up") return <ArrowUp className="size-3.5 text-emerald-400" />
  if (trend === "down") return <ArrowDown className="size-3.5 text-amber-400" />
  return <Minus className="size-3.5 text-muted-foreground" />
}

export function ReportShowcaseSection() {
  const t = useTranslations("report")

  return (
    <SectionContainer id="report" className="py-12 md:py-20">
      <div className="mb-8 space-y-2 text-center md:mb-12">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{t("title")}</h2>
        <p className="mx-auto max-w-2xl text-muted-foreground">{t("subtitle")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {reportMetrics.map((metric) => (
          <GlassCard key={metric.id} className="cyber-border">
            <div className="flex items-start justify-between">
              <p className="text-xs text-muted-foreground">{t(metric.labelKey)}</p>
              <TrendIcon trend={metric.trend} />
            </div>
            <StatCounter
              value={metric.value}
              suffix={metric.suffix}
              decimals={metric.suffix === "s" || metric.suffix === "%" ? 1 : 0}
              className="mt-2 text-2xl font-bold md:text-3xl"
            />
          </GlassCard>
        ))}
      </div>

      <GlassCard className="cyber-border mt-6">
        <h3 className="mb-4 text-sm font-semibold">{t("recentFindings")}</h3>
        <div className="space-y-3">
          {reportFindings.map((finding) => (
            <div
              key={finding.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <StatusTag variant={finding.severity === "critical" ? "danger" : finding.severity === "high" ? "warning" : finding.severity === "medium" ? "info" : "neutral"}>
                  {t(`severity.${finding.severity}`)}
                </StatusTag>
                <span className="text-sm">{t(finding.titleKey)}</span>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {t("resolved")}
              </span>
            </div>
          ))}
        </div>
      </GlassCard>
    </SectionContainer>
  )
}
